/*
 * Smooth scroll — Pause Rituals
 * -----------------------------------------------------------------------------
 * Adds eased momentum to MOUSE-WHEEL scrolling, and nothing else.
 *
 * Why not a scroll-hijacking library (Lenis, Locomotive):
 * those run a permanent rAF loop that owns the scroll position, so every scroll
 * lock in the theme (cart drawer, menu drawer, details-modal, pickup-availability,
 * header search) and every window.scrollTo / scrollIntoView call has to be routed
 * through them. This does the opposite — it only steps in for the one input that
 * actually feels abrupt, and steps straight back out:
 *
 *   • touch          — never intercepted, so mobile momentum, pull-to-refresh and
 *                      the iOS address-bar collapse are exactly as before
 *   • keyboard       — never intercepted (space, PageDown, arrows stay native)
 *   • scrollbar drag — never intercepted
 *   • trackpad       — never intercepted; the OS has already smoothed those deltas
 *                      and easing them again is what makes scrolling feel laggy
 *   • scrollTo /
 *     scrollIntoView — untouched; the loop bails the moment anything else scrolls
 *
 * Because it uses the real scroll position (not a transform on a wrapper), every
 * `position: sticky` element in the theme — the header, the PDP image column, the
 * ritual-bar box, facets — keeps working, as do IntersectionObserver reveals.
 *
 * Tuning comes from window.themeSmoothScroll, set in theme.liquid from the
 * "Scrolling" theme settings.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  /* ---- Opt out completely, rather than degrade ---------------------------- */

  // Someone who asked for less motion should get the browser's plain scroll.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Coarse pointers (phones, tablets) already have OS momentum scrolling.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  // The theme editor scrolls the page itself when a section is selected.
  if (window.Shopify && window.Shopify.designMode) return;

  var config = window.themeSmoothScroll || {};

  // How much of the remaining distance is covered each frame. Lower = longer,
  // slower glide. 0.12 lands in ~250ms at 60fps.
  var EASE = clamp(parseFloat(config.ease) || 0.12, 0.04, 0.5);

  // Multiplier on the wheel's own delta, so a notch can travel less than the
  // browser default if the page still feels quick.
  var DISTANCE = clamp(parseFloat(config.distance) || 1, 0.25, 2);

  /*
   * A mouse wheel fires a handful of large, widely spaced deltas (100px+ a notch
   * on Windows, 120 on many drivers). A trackpad fires a continuous stream of
   * small ones that the OS has already eased. Only the first kind needs help.
   */
  var WHEEL_MIN_DELTA = 45;

  var target = 0;
  var current = 0;
  var expected = -1;
  var frame = null;
  var running = false;

  /* ---- Helpers ------------------------------------------------------------ */

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function maxScroll() {
    return Math.max(0, root.scrollHeight - window.innerHeight);
  }

  // Every scroll lock in the theme lands one of these two classes on <body>.
  function isLocked() {
    var list = document.body.classList;
    return list.contains('overflow-hidden') || list.contains('header-search-open');
  }

  function normalizeDelta(event) {
    var delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16; // DOM_DELTA_LINE — Firefox
    else if (event.deltaMode === 2) delta *= window.innerHeight; // DOM_DELTA_PAGE
    return delta;
  }

  /*
   * Walk up from whatever the pointer is over. If anything on the way can still
   * scroll in this direction, the gesture belongs to it, not to the page — the
   * search results list, the cart drawer, the facets panel, a country selector.
   */
  function ownedByNestedScroller(node, delta) {
    while (node && node !== document.body && node !== root) {
      if (node.nodeType === 1 && node.scrollHeight > node.clientHeight + 1) {
        var overflowY = getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          var atTop = node.scrollTop <= 0;
          var atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          // Only hand it over if it has somewhere left to go that way.
          if (!((delta < 0 && atTop) || (delta > 0 && atBottom))) return true;
        }
      }
      node = node.parentNode;
    }
    return false;
  }

  /* ---- The loop ----------------------------------------------------------- */

  function apply(y) {
    expected = Math.round(y);
    // Explicit 'instant' so the `scroll-behavior: smooth` set on <html> for anchor
    // links doesn't try to animate each of these frames on top of the easing.
    window.scrollTo({ top: y, behavior: 'instant' });
  }

  function tick() {
    var distance = target - current;

    if (Math.abs(distance) < 0.5) {
      current = target;
      apply(current);
      stop();
      return;
    }

    current += distance * EASE;
    apply(current);
    frame = window.requestAnimationFrame(tick);
  }

  function start() {
    if (running) return;
    running = true;
    frame = window.requestAnimationFrame(tick);
  }

  function stop() {
    running = false;
    expected = -1;
    if (frame) window.cancelAnimationFrame(frame);
    frame = null;
  }

  /* ---- Input -------------------------------------------------------------- */

  function onWheel(event) {
    if (event.defaultPrevented) return;
    // ctrl/cmd + wheel is zoom; the modifiers are also used for horizontal scroll.
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (isLocked()) return;

    var delta = normalizeDelta(event);
    if (!delta) return;

    // Mostly-horizontal gesture: leave it alone (carousels, the category rail).
    if (Math.abs(event.deltaX) > Math.abs(delta)) return;

    // Trackpad — hand it straight back, and drop any glide still in flight so the
    // two never fight over the scroll position.
    if (Math.abs(delta) < WHEEL_MIN_DELTA) {
      stop();
      return;
    }

    if (ownedByNestedScroller(event.target, delta)) return;

    var limit = maxScroll();
    if (limit <= 0) return;

    event.preventDefault();

    // Idle: rebase on where the page actually is before adding to the target.
    if (!running) {
      current = window.scrollY;
      target = window.scrollY;
    }

    target = clamp(target + delta * DISTANCE, 0, limit);
    start();
  }

  /*
   * If the page moved somewhere we didn't put it, something else is driving —
   * a scrollIntoView, an anchor jump, the scrollbar, a focus change. Get out of
   * the way immediately.
   */
  function onScroll() {
    if (!running) return;
    if (Math.abs(window.scrollY - expected) > 3) stop();
  }

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('scroll', onScroll, { passive: true });

  // Any other expression of intent cancels the glide.
  window.addEventListener('mousedown', stop, { passive: true });
  window.addEventListener('keydown', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('resize', stop, { passive: true });
})();
